import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Check,
  Upload,
  AlertCircle,
  Eye,
  Plus,
  X,
  File,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Package,
  FileText,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import styles from './EditMaterialReceipt.module.css';
import NotFound from '../Error/NotFound';


const EditMaterialReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data State
  const [receiptData, setReceiptData] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    received_date: '',
    challan_number: '',
    transporter_name: '',
    vehicle_number: '',
    received_by: null,
    storage_location: '',
    status: 'partial',
    remarks: '',
    quality_remarks: '',
    mr_document: null
  });

  const [qualityCheck, setQualityCheck] = useState({
    is_outer_packing_intact: false,
    is_visual_damage_observed: false,
    is_matching_specifications: true,
    is_certification_enclosed: false
  });

  const [receivedItems, setReceivedItems] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    fetchData();
    return () => window.removeEventListener('resize', handleResize);
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [receiptRes, employeesRes] = await Promise.all([
        api.get(`/material-receipts/${id}`),
        api.get('/employees-dropdown')
      ]);

      if (employeesRes.data.success) {
        setEmployeeOptions(employeesRes.data.data.map(emp => ({
          value: emp.id,
          label: emp.employee_name
        })));
      }

      if (receiptRes.data.success) {
        const data = receiptRes.data.data;
        setReceiptData(data);

        // Populate form data
        setFormData({
          received_date: data.received_date || '',
          challan_number: data.challan_number || '',
          transporter_name: data.transporter_name || '',
          vehicle_number: data.vehicle_number || '',
          received_by: data.received_by ? { value: data.received_by.id, label: data.received_by.name } : null,
          storage_location: data.storage_location || '',
          status: data.status || 'partial',
          remarks: data.remarks || '',
          quality_remarks: data.quality_remarks || ''
        });

        // Set quality check flags
        setQualityCheck({
          is_outer_packing_intact: Boolean(data.is_outer_packing_intact),
          is_visual_damage_observed: Boolean(data.is_visual_damage_observed),
          is_matching_specifications: Boolean(data.is_matching_specifications),
          is_certification_enclosed: Boolean(data.is_certification_enclosed)
        });

        // Map items
        setReceivedItems((data.items || []).map(item => ({
          id: item.id,
          po_item_id: item.po_item_id || item.po_item?.id, // Store po_item_id with fallback
          name: item.po_item?.item_name || 'Standard Item',
          ordered: item.ordered_qty || 0,
          prev: item.prev_received_qty || 0,
          balance: item.balance_qty || 0,
          now: item.received_qty || 0,
          unit: item.po_item?.unit?.unit_name || 'Units'
        })));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load material receipt details');
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    setSubmitLoading(true);
    const fd = new FormData();
    fd.append('_method', 'PUT'); // Laravel requirement for PUT with FormData
    fd.append('purchase_order_id', receiptData?.purchase_order?.id || '');
    fd.append('status', formData.status);
    fd.append('received_date', formData.received_date);
    fd.append('received_by', formData.received_by?.value || '');
    fd.append('challan_number', formData.challan_number);
    fd.append('storage_location', formData.storage_location || '');
    fd.append('transporter_name', formData.transporter_name || '');
    fd.append('vehicle_number', formData.vehicle_number || '');
    fd.append('remarks', formData.remarks || '');
    fd.append('quality_remarks', formData.quality_remarks || '');

    Object.keys(qualityCheck).forEach(key => {
      fd.append(key, qualityCheck[key] ? 1 : 0);
    });

    receivedItems.forEach((item, index) => {
      const originalItem = receiptData?.items?.find(i => i.id === item.id);
      const resolvedPoItemId = item.po_item_id || originalItem?.po_item_id || originalItem?.po_item?.id;
      
      fd.append(`items[${index}][id]`, item.id);
      fd.append(`items[${index}][po_item_id]`, resolvedPoItemId);
      fd.append(`items[${index}][received_qty]`, item.now);
      if (item.remarks) {
        fd.append(`items[${index}][item_remarks]`, item.remarks);
      }
    });

    if (formData.mr_document) {
      fd.append('mr_document', formData.mr_document);
    }

    try {
      const response = await api.post(`/material-receipts/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success('Material receipt updated successfully!');
        navigate('/receipts');
      }
    } catch (error) {
      console.error('Error updating receipt:', error);
      toast.error(error.response?.data?.message || 'Failed to update receipt');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleQtyChange = (index, value) => {
    const newQty = parseFloat(value) || 0;
    setReceivedItems(prev => prev.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          now: newQty,
          // Recalculate balance real-time: Ordered - Previous - Current
          balance: parseFloat(item.ordered || 0) - parseFloat(item.prev || 0) - newQty
        };
      }
      return item;
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newFiles = files.map(file => ({
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
      }));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const newFiles = files.map(file => ({
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
      }));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const triggerFileInput = () => fileInputRef.current.click();

  if (loading) {
    return (
      <div className={`${styles['add-receipt-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={toggleSidebar} />
          <div className={styles['loading-overlay']}>
            <Loader2 className={styles['spinner']} />
            <span>Loading material receipt details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!receiptData) {
    return <NotFound />;
  }


  const po = receiptData?.purchase_order;

  return (
    <div className={`${styles['add-receipt-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}  ${isMobile ? styles['mobile-view'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <header className={styles['page-header']}>
            <div className={styles['header-left']}>
              <div className={styles['title-with-back']}>
                <div className={styles['header-title-section']}>
                  <h1>Edit Material Receipt</h1>
                  <p>Modifying record <span className={styles['highlight-po']}>{receiptData?.challan_number || `MR-${id}`}</span> for {po?.po_number || 'N/A'}</p>
                </div>
              </div>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['view-po-btn']} onClick={() => navigate(`/purchase-orders/view/${po?.id}`)}>View LOI Details</button>
            </div>
          </header>

          {/* Section 1: LOI Summary */}
          <section className={styles['section-title']}>
            <span>Section 1: LOI Summary</span>
            <span className={styles['status-tag']}>Active</span>
          </section>
          <div className={styles['form-section-card']}>
            <div className={styles['po-summary-grid']}>
              <div className={styles['summary-item-receipts']}>
                <label>LOI (Letter of Intent) #</label>
                <span className={styles['value']}>{po?.po_number || '---'}</span>
              </div>
              <div className={styles['summary-item-receipts']}>
                <label>Vendor</label>
                <span className={styles['value']}>{receiptData?.vendor_name || po?.vendor?.vendor_name || '---'}</span>
              </div>
              <div className={styles['summary-item-receipts']}>
                <label>Project</label>
                <span className={styles['value']}>{po?.project?.project_name || '---'}</span>
              </div>
              <div className={[styles['summary-item-receipts'], styles['completion-status']].join(' ')}>
                <label>Total Value</label>
                <span className={styles['value']}>₹ {parseFloat(po?.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Material Items Receipt Table */}
          <section className={styles['section-title']}>
            <span>Section 2: Material Items Receipt Table</span>
          </section>
          <div className={`${styles['form-section-card']} ${isMobile ? styles['no-bg-mobile'] : ''}`} style={{ padding: isMobile ? '0' : '0' }}>
            {!isMobile ? (
              <table className={styles['items-table']}>
                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Ordered Qty</th>
                    <th>Prev. Received</th>
                    <th>Balance Qty</th>
                    <th>Receiving Now</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {receivedItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className={styles['item-name']}>{item.name}</td>
                      <td className={styles['qty-value']}>{item.ordered}</td>
                      <td className={styles['qty-value']}>{item.prev}</td>
                      <td className={styles['balance-highlight']}>{item.balance}</td>
                      <td>
                        <input
                          type="number"
                          className={styles['receiving-input']}
                          value={item.now}
                          onChange={(e) => handleQtyChange(index, e.target.value)}
                        />
                      </td>
                      <td className={styles['unit-label']}>{item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles['item-cards-mobile']}>
                {receivedItems.map((item, index) => (
                  <div key={item.id || index} className={styles['item-card-mobile']}>
                    <div className={styles['item-card-header']}>
                      <span className={styles['item-name-mobile']}>{item.name}</span>
                      <span className={styles['unit-pill-mobile']}>{item.unit}</span>
                    </div>
                    
                    <div className={styles['item-card-grid-mobile']}>
                      <div className={styles['card-item-stat']}>
                        <label>Ordered</label>
                        <span>{item.ordered}</span>
                      </div>
                      <div className={styles['card-item-stat']}>
                        <label>Prev. Rec.</label>
                        <span>{item.prev}</span>
                      </div>
                      <div className={`${styles['card-item-stat']} ${item.balance > 0 ? styles['has-balance'] : ''}`}>
                        <label>Balance</label>
                        <span>{item.balance}</span>
                      </div>
                    </div>

                    <div className={styles['card-receiving-action']}>
                      <label>Receiving Now</label>
                      <div className={styles['input-with-max']}>
                        <input
                          type="number"
                          className={styles['receiving-input-mobile']}
                          value={item.now}
                          onChange={(e) => handleQtyChange(index, e.target.value)}
                        />
                        <span className={styles['max-hint']}>max {item.ordered - item.prev}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 3 & 4 Grid */}
          <div className={styles['forms-grid']}>
            <div className={styles['left-form']}>
              <section className={styles['section-title']}>
                <span>Section 3: Receipt Details</span>
              </section>
              <div className={styles['form-section-card']}>
                <div className={styles['input-grid']}>
                  <div className={styles['form-group']}>
                    <label>Date Received <span className={styles['required']}>*</span></label>
                    <CustomDatePicker
                      selected={formData.received_date}
                      onChange={(date) => setFormData(prev => ({ ...prev, received_date: date.toISOString().split('T')[0] }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Challan / Invoice # <span className={styles['required']}>*</span></label>
                    <input 
                      type="text" 
                      value={formData.challan_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, challan_number: e.target.value }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Transporter Name</label>
                    <input
                      type="text"
                      value={formData.transporter_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, transporter_name: e.target.value }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Status <span className={styles['required']}>*</span></label>
                    <CustomSelect
                      options={[
                        { value: 'partial', label: 'Partial Shipment' },
                        { value: 'completed', label: 'Fully Completed' }
                      ]}
                      value={formData.status ? { 
                        value: formData.status, 
                        label: formData.status === 'partial' ? 'Partial Shipment' : (formData.status === 'completed' ? 'Fully Completed' : formData.status.toUpperCase())
                      } : null}
                      onChange={(option) => setFormData(prev => ({ ...prev, status: option.value }))}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Received By <span className={styles['required']}>*</span></label>
                    <CustomSelect
                      options={employeeOptions}
                      value={formData.received_by}
                      onChange={(opt) => setFormData(prev => ({ ...prev, received_by: opt }))}
                      placeholder="Select Employee..."
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Storage Location <span className={styles['required']}>*</span></label>
                    <input 
                      type="text" 
                      value={formData.storage_location}
                      onChange={(e) => setFormData(prev => ({ ...prev, storage_location: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles['right-form']}>
              <section className={styles['section-title']}>
                <span>Section 4: Quality Check</span>
              </section>
              <div className={styles['form-section-card']}>
                <div className={styles['checklist-grid']}>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_outer_packing_intact ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_outer_packing_intact: !qualityCheck.is_outer_packing_intact })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_outer_packing_intact && <Check size={14} />}</div>
                    <span>Outer Packing Intact</span>
                  </div>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_visual_damage_observed ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_visual_damage_observed: !qualityCheck.is_visual_damage_observed })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_visual_damage_observed && <Check size={14} />}</div>
                    <span>Visual Damage Observed</span>
                  </div>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_matching_specifications ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_matching_specifications: !qualityCheck.is_matching_specifications })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_matching_specifications && <Check size={14} />}</div>
                    <span>Matching Specifications</span>
                  </div>
                  <div
                    className={`${styles['checklist-item']} ${qualityCheck.is_certification_enclosed ? styles['checked'] : ''}`}
                    onClick={() => setQualityCheck({ ...qualityCheck, is_certification_enclosed: !qualityCheck.is_certification_enclosed })}
                  >
                    <div className={styles['check-box']}>{qualityCheck.is_certification_enclosed && <Check size={14} />}</div>
                    <span>Certification Enclosed</span>
                  </div>
                </div>

                <div className={styles['form-group']}>
                  <label>Quality Remarks</label>
                  <textarea
                    value={formData.quality_remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, quality_remarks: e.target.value }))}
                    placeholder="Add any quality related observations..."
                  ></textarea>
                </div>

                <div className={styles['upload-section']}>
                  <label>Upload Document/Photo</label>
                  
                  <div className={styles['upload-area']} style={{ cursor: 'default' }}>
                    <Upload size={24} className={styles['upload-icon']} />
                    <span className={styles['upload-text']}>Select source to upload</span>
                    <span className={styles['upload-hint']}>PNG, JPG up to 10MB</span>
                    
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', width: '100%', justifyContent: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        id="camera-upload"
                        onChange={(e) => setFormData(prev => ({ ...prev, mr_document: e.target.files[0] }))}
                      />
                      <label htmlFor="camera-upload" className={styles['btn-cancel']} style={{ flex: 1, maxWidth: '160px', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', background: 'white', margin: 0 }}>
                        <Camera size={18} /> Camera
                      </label>

                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        id="gallery-upload"
                        onChange={(e) => setFormData(prev => ({ ...prev, mr_document: e.target.files[0] }))}
                      />
                      <label htmlFor="gallery-upload" className={styles['btn-cancel']} style={{ flex: 1, maxWidth: '160px', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', background: 'white', margin: 0 }}>
                        <ImageIcon size={18} /> Gallery
                      </label>
                    </div>
                  </div>

                  {formData.mr_document && (
                    <div className={styles['file-list']}>
                      <div className={styles['file-item']}>
                        <div className={styles['file-info']}>
                          <File size={16} className={styles['file-icon']} />
                          <div className={styles['file-details']}>
                            <span className={styles['file-name']}>{formData.mr_document.name}</span>
                            <span className={styles['file-size']}>{(formData.mr_document.size / (1024 * 1024)).toFixed(2)} MB</span>
                          </div>
                        </div>
                        <button type="button" className={styles['remove-file-btn']} onClick={() => setFormData(prev => ({ ...prev, mr_document: null }))}>
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  {receiptData?.mr_document_filepath && !formData.mr_document && (
                    <div className={styles['file-list']}>
                      <div className={styles['file-item']}>
                        <div className={styles['file-info']}>
                          <File size={16} className={styles['file-icon']} />
                          <div className={styles['file-details']}>
                            <span className={styles['file-name']}>{receiptData.mr_document_filename || 'Document Attached'}</span>
                            <span className={styles['file-size']}>Existing File</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={[styles['form-group'], styles['mt-md']].join(' ')}>
                  <label>Internal Audit Remarks</label>
                  <textarea
                    placeholder="Internal warehouse or logistics notes..."
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    className={styles['audit-remarks']}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <footer className={styles['form-footer']}>
            <div className={styles['footer-hint']}>
              <AlertCircle size={18} color="#f59e0b" />
              <span>Submission will automatically update the LOI (Letter of Intent) fulfillment status.</span>
            </div>
            <div className={styles['footer-actions']}>
              <button className={styles['btn-cancel']} onClick={() => navigate(-1)}>Cancel</button>
              <button
                className={styles['btn-submit']}
                onClick={handleSubmit}
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <>
                    <Loader2 className={styles['spinner']} size={18} />
                    <span>Updating Receipt...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} />
                    <span>Update Material Receipt</span>
                  </>
                )}
              </button>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default EditMaterialReceipt;
